import {
  InvoiceActionVerb,
  PartnerInvoiceStatus,
  PartnerInvoiceType,
} from 'src/constants/invoice/enums'
import type { ComposedInvoice, PartnerInvoice } from 'src/interfaces/invoice'

/** Menu order, shared by both actions cells. Cancel first: it is the one with a typed guard. */
const VERB_ORDER = [
  InvoiceActionVerb.Cancel,
  InvoiceActionVerb.MarkFunded,
  InvoiceActionVerb.MarkFundedNoCheck,
  InvoiceActionVerb.ProcessPayment,
  InvoiceActionVerb.MarkRepaid,
  InvoiceActionVerb.RepayFees,
  InvoiceActionVerb.RepayDueInterest,
]

/**
 * The backend rejects these outright for a non-loan invoice
 * ('Only loan invoices can have fees repayment' / '…can have due interest'), and an invoice's
 * type never changes — so hiding them is safe.
 */
const LOAN_ONLY_VERBS = [InvoiceActionVerb.RepayFees, InvoiceActionVerb.RepayDueInterest]

/**
 * Only STRUCTURAL rules are applied here: loan-only verbs, and nothing at all for a canceled
 * invoice.
 *
 * Everything else — mark-funded needing `loanFundingStatus === SUCCEEDED`, cancel needing
 * `created`, process-payment and repayment needing `funded`, the auto-funding-in-progress
 * guard, Oatfi's principal-balance check — is deliberately NOT mirrored. Those rules read
 * state that can change between the page load and the click, and a second implementation here
 * would disable a legitimate action (or enable an illegitimate one) the moment the two drift.
 * The backend's own message is surfaced per invoice in the results list instead.
 */
export const getAvailableInvoiceActions = (invoice: PartnerInvoice): InvoiceActionVerb[] => {
  if (invoice.status === PartnerInvoiceStatus.Canceled) {
    return []
  }

  return VERB_ORDER.filter((verb) => {
    if (LOAN_ONLY_VERBS.includes(verb)) {
      return invoice.type === PartnerInvoiceType.Loan
    }

    return true
  })
}

/** The union across the nested invoices, in canonical verb order. */
export const getAvailableComposedInvoiceActions = (
  invoice: ComposedInvoice,
): InvoiceActionVerb[] => {
  return VERB_ORDER.filter((verb) => {
    return invoice.partnerInvoices.some((partnerInvoice) => {
      return getAvailableInvoiceActions(partnerInvoice).includes(verb)
    })
  })
}

/**
 * Which partner invoices a composed-level verb runs against. Canceled rows are skipped because
 * nothing can move them; a loan-only verb is narrowed to loan rows. Everything else is sent and
 * allowed to be rejected — that is what the per-invoice results list reports.
 */
export const getTargetInvoiceIds = (
  invoice: ComposedInvoice,
  verb: InvoiceActionVerb,
): string[] => {
  return invoice.partnerInvoices
    .filter((partnerInvoice) => {
      return getAvailableInvoiceActions(partnerInvoice).includes(verb)
    })
    .map((partnerInvoice) => {
      return partnerInvoice.id
    })
}
